"use client";

import { useRef, memo, useEffect, useState, useMemo, useCallback, use } from "react";
import { Filter } from "@/lib/interfaces/cards";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { slugify, transformForURL, buildURL } from "../utils/filterUtils";

type UIFilter = Filter;

interface FiltersSidebarProps {
  filters?: UIFilter[];
  filtersPromise?: Promise<UIFilter[]>;
  isSidebarCollapsed?: boolean;
  expandedGroups?: Record<string, boolean>;
  selectedFilterIds?: string[];
  toggleSidebar?: () => void;
  closeSidebar?: () => void;
  toggleFilterGroup?: (filter: UIFilter) => void;
  onFilterChange?: (optionId: string, checked: boolean) => void;
}

function FiltersSidebar({
  filters: initialFilters = [],
  filtersPromise,
  isSidebarCollapsed = true,
  expandedGroups = {},
  selectedFilterIds,
  toggleSidebar,
  closeSidebar,
  toggleFilterGroup,
  onFilterChange
}: FiltersSidebarProps) {
  const filters = filtersPromise ? use(filtersPromise) : initialFilters;
  const sidebarRef = useRef<HTMLDivElement>(null);

  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // Mappings for standalone URL-driven transitions
  const flat = useMemo(() => {
    return filters.flatMap((group) => {
      const gslug = slugify(group.filter_name);
      return group.values.map((v) => ({
        id: v.id,
        value: v.value,
        groupSlug: gslug,
      }));
    });
  }, [filters]);

  const idToValue = useMemo(() => Object.fromEntries(flat.map((o) => [o.id.toString(), o.value])), [flat]);
  const idToGroupSlug = useMemo(() => Object.fromEntries(flat.map((o) => [o.id.toString(), o.groupSlug])), [flat]);

  const groupToValueToId = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};
    for (const o of flat) {
      map[o.groupSlug] ||= {};
      map[o.groupSlug][transformForURL(o.value)] = o.id.toString();
    }
    return map;
  }, [flat]);

  // Derived filter IDs from URL searchParams (fallback if prop not provided)
  const resolvedSelectedFilterIds = useMemo(() => {
    const inputIds = selectedFilterIds !== undefined ? selectedFilterIds.map(x => x.toString()) : undefined;
    if (inputIds !== undefined) return inputIds;

    const ids: string[] = [];
    const groups = Array.from(new Set(flat.map((o) => o.groupSlug)));
    for (const gslug of groups) {
      const rawValue = searchParams.get(gslug);
      if (rawValue) {
        const values = rawValue.split(",");
        const v2id = groupToValueToId[gslug] || {};
        for (const value of values) {
          const id = v2id[value];
          if (id) ids.push(id.toString());
        }
      }
    }
    return ids;
  }, [selectedFilterIds, searchParams, groupToValueToId, flat]);

  // Localized states for drawer and groups (fallback if callbacks/props not provided)
  const [localIsSidebarCollapsed, setLocalIsSidebarCollapsed] = useState(isSidebarCollapsed);
  const [localExpandedGroups, setLocalExpandedGroups] = useState<Record<string, boolean>>(() => {
    if (Object.keys(expandedGroups).length > 0) return expandedGroups;
    return filters.length > 0 ? { [filters[0].filter_name]: true } : {};
  });

  // Sync state if props change
  useEffect(() => {
    setLocalIsSidebarCollapsed(isSidebarCollapsed);
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (Object.keys(expandedGroups).length > 0) {
      setLocalExpandedGroups(expandedGroups);
    }
  }, [expandedGroups]);

  // Expand the first filter group when filters load asynchronously
  useEffect(() => {
    if (filters.length > 0 && Object.keys(localExpandedGroups).length === 0) {
      setLocalExpandedGroups({ [filters[0].filter_name]: true });
    }
  }, [filters, localExpandedGroups]);


  const activeIsSidebarCollapsed = toggleSidebar ? isSidebarCollapsed : localIsSidebarCollapsed;
  const activeExpandedGroups = toggleFilterGroup ? expandedGroups : localExpandedGroups;

  // Prevent scrolling when mobile sidebar is open
  useEffect(() => {
    if (!activeIsSidebarCollapsed && typeof window !== "undefined" && window.innerWidth < 1024) {
      document.body.style.overflow = "hidden";
    } else if (typeof document !== "undefined") {
      document.body.style.overflow = "";
    }
    return () => {
      if (typeof document !== "undefined") document.body.style.overflow = "";
    };
  }, [activeIsSidebarCollapsed]);

  const handleToggleSidebar = () => {
    if (toggleSidebar) toggleSidebar();
    else setLocalIsSidebarCollapsed(!localIsSidebarCollapsed);
  };

  const handleCloseSidebar = () => {
    if (closeSidebar) closeSidebar();
    else setLocalIsSidebarCollapsed(true);
  };

  const handleToggleFilterGroup = useCallback((filter: UIFilter) => {
    if (toggleFilterGroup) {
      toggleFilterGroup(filter);
    } else {
      setLocalExpandedGroups((prev) => ({
        ...prev,
        [filter.filter_name]: !prev[filter.filter_name],
      }));
    }
  }, [toggleFilterGroup]);

  const handleFilterChange = (optionId: string, checked: boolean) => {
    if (onFilterChange) {
      onFilterChange(optionId, checked);
      return;
    }

    // Direct standalone URL routing update
    const groupSlug = idToGroupSlug[optionId];
    if (!groupSlug) return;

    const currentParams = new URLSearchParams(searchParams.toString());

    currentParams.delete("page"); // new filter or sort starts again at page 1
    const currentRaw = currentParams.get(groupSlug) || "";
    const currentValues = currentRaw ? currentRaw.split(",") : [];
    const optionValue = transformForURL(idToValue[optionId]);

    let newValues: string[];
    if (checked) {
      newValues = [...currentValues, optionValue];
    } else {
      newValues = currentValues.filter((v) => v !== optionValue);
    }

    if (newValues.length === 0) currentParams.delete(groupSlug);
    else currentParams.set(groupSlug, newValues.join(","));

    router.replace(buildURL(pathname, currentParams), { scroll: false });
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        className={`lg:hidden mb-3 flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm text-sm font-medium text-gray-700 active:scale-95 transition-all hover:bg-gray-50`}
        onClick={handleToggleSidebar}
        aria-label="Open filters"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 text-primary-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
        Filters
        {resolvedSelectedFilterIds.length > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-400 text-xs text-white">
            {resolvedSelectedFilterIds.length}
          </span>
        )}
      </button>

      {/* Overlay (Mobile) */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity lg:hidden ${activeIsSidebarCollapsed ? "opacity-0 pointer-events-none" : "opacity-100 pointer-events-auto"
          }`}
        onClick={handleCloseSidebar}
      />

      {/* Sidebar Container */}
      <div
        ref={sidebarRef}
        className={`
          fixed inset-y-0 left-0 z-50 w-[280px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out lg:relative lg:transform-none lg:z-0 lg:w-60 lg:shrink-0 lg:bg-transparent lg:shadow-none lg:translate-x-0
          ${activeIsSidebarCollapsed ? "-translate-x-full" : "translate-x-0"}
          lg:block
        `}
      >
        <div className="flex flex-col h-full lg:h-auto">
          {/* Sidebar Header (Mobile) */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 lg:hidden">
            <h2 className="text-lg font-bold text-gray-900">Filters</h2>
            <button
              type="button"
              onClick={handleCloseSidebar}
              aria-label="Close filters"
              className="p-2 text-gray-600 hover:text-gray-500 rounded-full hover:bg-gray-100 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Filters Content */}
          <div className="flex-1 overflow-y-auto lg:overflow-visible p-4 lg:p-0">
            <div className="filters-sidebar space-y-3 lg:sticky lg:top-24">
              {filters.map((filter) => (
                <div
                  key={filter.filter_name}
                  className="rounded-2xl border border-paper-rule bg-paper p-4"
                >
                  <div
                    className="flex justify-between items-center cursor-pointer group"
                    onClick={() => handleToggleFilterGroup(filter)}
                  >
                    <h3 className="font-sans text-xs font-semibold uppercase tracking-[0.14em] text-gray-700">
                      {filter.filter_name}
                    </h3>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 text-gray-600 transition-transform duration-300 group-hover:text-primary-400 ${activeExpandedGroups[filter.filter_name] ? "rotate-180" : ""
                        }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>

                  <div
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      maxHeight: activeExpandedGroups[filter.filter_name] ? "1000px" : "0px",
                      opacity: activeExpandedGroups[filter.filter_name] ? 1 : 0,
                      marginTop: activeExpandedGroups[filter.filter_name] ? "1rem" : "0px",
                    }}
                  >
                    <div className="space-y-2.5">
                      {filter.values.map((option) => (
                        <label
                          key={option.id}
                          className="flex items-center group cursor-pointer"
                        >
                          <div className="relative flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 bg-white checked:bg-primary-400 checked:border-primary-400 transition-all focus:ring-1 focus:ring-primary-200"
                              value={option.id}
                              checked={resolvedSelectedFilterIds.includes(option.id)}
                              onChange={(e) => handleFilterChange(option.id, e.target.checked)}
                            />
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                          <span className="ml-3 text-sm font-medium text-gray-600 group-hover:text-primary-400 transition-colors">
                            {option.value}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default memo(FiltersSidebar);